// Canvas store for React Flow workflow editor
// Implements workflow canvas state management with persistence

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { Node as FlowNode, Edge, Viewport } from "reactflow";

import { logger } from "../services/logger";
import { generateUUID } from "../utils/uuid";
import { DateTime } from "luxon";
import { WorkflowNode } from "@/components/workflows/types";

export interface HistoryState {
  nodes: FlowNode<WorkflowNode>[];
  edges: Edge[];
  paramChanges?: Array<{
    nodeId: string;
    paramName: string;
    prevValue: any;
    newValue: any;
  }>;
  viewport: Viewport;
  timestamp: DateTime;
  isLastSaved?: boolean;
}

interface CanvasState {
  nodes: FlowNode<WorkflowNode>[];
  edges: Edge[];
  selectedNodeId: string | null;
  isExecuting: boolean;
  workflowId: string;
  viewport: Viewport;
  // History state variables only
  history: HistoryState[];
  historyPointer: number;
}

interface WorkflowCanvasStore extends CanvasState {
  // Actions
  setNodes: (nodes: FlowNode<WorkflowNode>[]) => void;
  addNode: (node: FlowNode<WorkflowNode>) => void;
  updateNode: (id: string, updates: Partial<FlowNode<WorkflowNode>>) => void;
  removeNode: (id: string) => void;
  setEdges: (edges: Edge[]) => void;
  addEdge: (edge: Edge) => void;
  removeEdge: (id: string) => void;
  setSelectedNodeId: (nodeId: string | null) => void;
  setExecuting: (executing: boolean) => void;
  resetCanvasState: (clearHistory?: boolean) => void;
  loadWorkflow: (
    nodes: FlowNode<WorkflowNode>[],
    edges: Edge[],
    viewport?: Viewport,
  ) => void;
  setViewport: (viewport: Viewport) => void;
  setWorkflowId: (workflowId: string) => void;
  // Animation helpers
  setAllNodesClassName: (className: string | undefined) => void;
  // History state getters/setters only
  setHistory: (history: HistoryState[]) => void;
  setHistoryPointer: (pointer: number) => void;
}

const initialState: CanvasState = {
  nodes: [],
  edges: [],
  selectedNodeId: null,
  isExecuting: false,
  workflowId: "",
  viewport: { x: 0, y: 0, zoom: 1 },
  history: [],
  historyPointer: 0,
};

export const useWorkflowCanvasStore = create<WorkflowCanvasStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setNodes: (nodes) => {
          logger.debug("Canvas nodes updated", {
            count: nodes.length,
            correlationId: generateUUID(),
          });
          set({ nodes }, false, "setNodes");
        },

        addNode: (node) => {
          const { nodes } = get();
          logger.info("Node added to canvas", {
            nodeId: node.id,
            nodeType: node.type,
            correlationId: generateUUID(),
          });
          set({ nodes: [...nodes, node] }, false, "addNode");
        },

        updateNode: (id, updates) => {
          const { nodes } = get();
          const updatedNodes = nodes.map((node) =>
            node.id === id ? { ...node, ...updates } : node,
          );
          logger.debug("Canvas node updated", {
            nodeId: id,
            updates: Object.keys(updates),
            correlationId: generateUUID(),
          });
          set({ nodes: updatedNodes }, false, "updateNode");
        },

        removeNode: (id) => {
          const { nodes, edges } = get();
          const filteredNodes = nodes.filter((node) => node.id !== id);
          const filteredEdges = edges.filter(
            (edge) => edge.source !== id && edge.target !== id,
          );
          logger.info("Node removed from canvas", {
            nodeId: id,
            correlationId: generateUUID(),
          });
          set(
            {
              nodes: filteredNodes,
              edges: filteredEdges,
              selectedNodeId:
                get().selectedNodeId === id ? null : get().selectedNodeId,
            },
            false,
            "removeNode",
          );
        },

        setEdges: (edges) => {
          logger.debug("Canvas edges updated", {
            count: edges.length,
            correlationId: generateUUID(),
          });
          set({ edges }, false, "setEdges");
        },

        addEdge: (edge) => {
          const { edges } = get();
          logger.info("Edge added to canvas", {
            edgeId: edge.id,
            source: edge.source,
            target: edge.target,
            correlationId: generateUUID(),
          });
          set({ edges: [...edges, edge] }, false, "addEdge");
        },

        removeEdge: (id) => {
          const { edges } = get();
          const filteredEdges = edges.filter((edge) => edge.id !== id);
          logger.info("Edge removed from canvas", {
            edgeId: id,
            correlationId: generateUUID(),
          });
          set({ edges: filteredEdges }, false, "removeEdge");
        },

        setSelectedNodeId: (nodeId) => {
          logger.debug("Node selection changed", {
            nodeId,
            correlationId: generateUUID(),
          });
          set({ selectedNodeId: nodeId }, false, "setSelectedNodeId");
        },

        setExecuting: (isExecuting) => {
          logger.info("Canvas execution state changed", {
            isExecuting,
            correlationId: generateUUID(),
          });
          set({ isExecuting }, false, "setExecuting");
        },

        resetCanvasState: (clearHistory?: boolean) => {
          logger.info("Canvas cleared", {
            correlationId: generateUUID(),
          });
          set(
            {
              ...initialState,
              workflowId: get().workflowId,
              history: clearHistory
                ? []
                : [
                    ...get().history,
                    {
                      nodes: initialState.nodes,
                      edges: initialState.edges,
                      viewport: initialState.viewport,
                      timestamp: DateTime.now(),
                      isLastSaved: false,
                    },
                  ],
              historyPointer: clearHistory ? 0 : get().historyPointer + 1,
            },
            false,
            "clearCanvas",
          );
        },

        loadWorkflow: (nodes, edges, viewport) => {
          logger.info("Workflow loaded to canvas", {
            nodeCount: nodes.length,
            edgeCount: edges.length,
            correlationId: generateUUID(),
          });
          const newState: Partial<CanvasState> = {
            nodes,
            edges,
            selectedNodeId: null,
          };
          if (viewport) {
            newState.viewport = viewport;
          }
          set(newState, false, "loadWorkflow");
        },

        setViewport: (viewport) => {
          logger.debug("Canvas viewport updated", {
            viewport,
            correlationId: generateUUID(),
          });
          set({ viewport }, false, "setViewport");
        },

        setWorkflowId: (workflowId) => {
          logger.info("Canvas workflow ID set", {
            workflowId,
            correlationId: generateUUID(),
          });
          set({ workflowId }, false, "setWorkflowId");
        },

        // Animation helpers
        setAllNodesClassName: (className) => {
          const { nodes } = get();
          const updatedNodes = nodes.map((node) => ({
            ...node,
            className,
          }));
          set({ nodes: updatedNodes }, false, "setAllNodesClassName");
        },

        // History state setters only
        setHistory: (history) => {
          set({ history }, false, "setHistory");
        },

        setHistoryPointer: (historyPointer) => {
          set({ historyPointer }, false, "setHistoryPointer");
        },
      }),
      {
        name: "canvas-store",
        partialize: (state) => ({
          workflowId: state.workflowId,
          nodes: state.nodes,
          edges: state.edges,
          viewport: state.viewport,
          selectedNodeId: state.selectedNodeId,
          history: state.history,
          historyPointer: state.historyPointer,
        }),
      },
    ),
    {
      name: "canvas-store-devtools",
      enabled: import.meta.env.DEV,
    },
  ),
);
